// ChartCard.jsx
import React, { useState, useMemo } from "react";
import { Card, Radio, Table } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useSelector } from "react-redux";
const ChartCard = ({
  title,
  dataKey,
  color,
  data,
  selected,
  onClick,
  colornumber,
}) => {
  const [view, setView] = useState("week");
  const [displayType, setDisplayType] = useState("chart"); // "chart" or "table"

  const allcampaign = useSelector(
    (state) => state?.campaignReducer?.Allcampaign
  );
  const rawData = allcampaign?.data || [];
  const dataYearly = allcampaign?.isLoginFalse?.yearly;
  const dataMonthly = allcampaign?.isLoginFalse?.monthly;
  const dataWeekly = allcampaign?.isLoginFalse?.weekly;
  const dataDaily = allcampaign?.isLoginFalse?.daily;

  const handleViewChange = (e) => {
    setView(e.target.value);
  };

  // Helper function to process data by time period
  const processDataByPeriod = (period) => {
    const now = new Date();
    // Check if rawData exists and has items
    if (!rawData || rawData.length === 0) {
      return null;
    }
    let filteredData = rawData.filter((item) => item.islogin === false);

    if (!filteredData || filteredData.length === 0) {
      return null;
    }

    switch (period) {
      case "day": {
        // Last 24 hours by hour
        const hoursAgo24 = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        filteredData = filteredData.filter((item) => {
          const itemDate = new Date(item.createdAt);
          return itemDate >= hoursAgo24 && itemDate <= now;
        });

        // Initialize last 24 hours chronologically (from oldest to newest)
        const hourData = [];
        // Create entries from 23 hours ago to now (oldest first)
        for (let i = 0; i < 24; i++) {
          const date = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000);
          const hour = String(date.getHours()).padStart(2, "0");
          const hourKey = date.toISOString().slice(0, 13); // YYYY-MM-DDTHH format
          hourData.push({ hourKey, hour, timestamp: date.getTime(), value: 0 });
        }

        // Group by hour (match by hourKey to ensure correct hour slot)
        filteredData.forEach((item) => {
          const itemDate = new Date(item.createdAt);
          const hourKey = itemDate.toISOString().slice(0, 13); // YYYY-MM-DDTHH format
          const hourEntry = hourData.find((h) => h.hourKey === hourKey);
          if (hourEntry) {
            hourEntry.value += 1;
          }
        });

        // Return in chronological order (explicitly sort by timestamp to ensure correct order)
        return hourData
          .sort((a, b) => a.timestamp - b.timestamp)
          .map((entry) => ({
            name: `${entry.hour}:00`,
            value: entry.value,
          }));
      }

      case "week": {
        // Last 7 days by day
        const daysAgo7 = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filteredData = filteredData.filter((item) => {
          const itemDate = new Date(item.createdAt);
          return itemDate >= daysAgo7 && itemDate <= now;
        });

        // Initialize 7 days chronologically
        const dayData = [];
        const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        for (let i = 6; i >= 0; i--) {
          const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const dayKey = date.toISOString().split("T")[0];
          const dayName = dayNames[date.getDay()];
          dayData.push({
            dayKey,
            timestamp: date.getTime(),
            name: dayName,
            value: 0,
          });
        }

        // Group by day
        filteredData.forEach((item) => {
          const itemDate = new Date(item.createdAt);
          const dayKey = itemDate.toISOString().split("T")[0];
          const dayEntry = dayData.find((d) => d.dayKey === dayKey);
          if (dayEntry) {
            dayEntry.value += 1;
          }
        });

        // Return sorted chronologically
        return dayData
          .sort((a, b) => a.timestamp - b.timestamp)
          .map((entry) => ({ name: entry.name, value: entry.value }));
      }

      case "month": {
        // Last 30 days by day
        const daysAgo30 = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        filteredData = filteredData.filter((item) => {
          const itemDate = new Date(item.createdAt);
          return itemDate >= daysAgo30 && itemDate <= now;
        });

        // Initialize 30 days chronologically
        const dayData = [];
        for (let i = 29; i >= 0; i--) {
          const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
          const dayKey = date.toISOString().split("T")[0];
          const dayLabel = date.getDate();
          dayData.push({
            dayKey,
            timestamp: date.getTime(),
            name: String(dayLabel),
            value: 0,
          });
        }

        // Group by day
        filteredData.forEach((item) => {
          const itemDate = new Date(item.createdAt);
          const dayKey = itemDate.toISOString().split("T")[0];
          const dayEntry = dayData.find((d) => d.dayKey === dayKey);
          if (dayEntry) {
            dayEntry.value += 1;
          }
        });

        // Sort chronologically and intelligently select days to display
        const sortedDays = dayData.sort((a, b) => a.timestamp - b.timestamp);

        // Apply smart thinning: show important days only
        const maxPoints = 15; // Maximum number of points to show

        // Find days with data (non-zero) and days where value changes
        const importantDays = new Set([0]); // First day index

        for (let i = 1; i < sortedDays.length - 1; i++) {
          const prev = sortedDays[i - 1];
          const curr = sortedDays[i];
          const next = sortedDays[i + 1];

          // Include if:
          // 1. Has clicks (value > 0)
          // 2. Value changes from previous or to next (peak/valley)
          if (
            curr.value > 0 ||
            curr.value !== prev.value ||
            curr.value !== next.value
          ) {
            importantDays.add(i);
          }
        }

        // Always include last day
        if (sortedDays.length > 1) {
          importantDays.add(sortedDays.length - 1);
        }

        // If we have too many important days, thin further
        if (importantDays.size > maxPoints) {
          // Keep first, last, and evenly spaced important days
          const importantArray = Array.from(importantDays).sort(
            (a, b) => a - b
          );
          const step = Math.ceil(importantArray.length / maxPoints);
          const thinnedIndices = new Set();

          // Always include first and last
          thinnedIndices.add(importantArray[0]);
          thinnedIndices.add(importantArray[importantArray.length - 1]);

          // Include evenly spaced important days
          for (let i = step; i < importantArray.length - 1; i += step) {
            thinnedIndices.add(importantArray[i]);
          }

          // Fill gaps: ensure we don't skip too many consecutive days
          for (let i = 1; i < importantArray.length - 1; i++) {
            const curr = importantArray[i];
            const prev = importantArray[i - 1];
            const next = importantArray[i + 1];

            // If gap is too large, include middle point
            if (curr - prev > 5 && !thinnedIndices.has(curr)) {
              thinnedIndices.add(Math.floor((curr + prev) / 2));
            }
            if (next - curr > 5 && !thinnedIndices.has(curr)) {
              thinnedIndices.add(Math.floor((curr + next) / 2));
            }
          }

          return sortedDays
            .filter((_, index) => thinnedIndices.has(index))
            .map((entry) => ({
              name: entry.name,
              value: entry.value,
            }));
        }

        // Return important days
        return sortedDays
          .filter((_, index) => importantDays.has(index))
          .map((entry) => ({
            name: entry.name,
            value: entry.value,
          }));
      }

      // Old thinning logic (commented out to match campaign details page)
      /*
        // If we have many days (30), intelligently thin them out
        const shouldThin = sortedDays.length > 15;
        if (shouldThin) {
          const thinned = [];
          const firstIndex = 0;
          const lastIndex = sortedDays.length - 1;

          // Always include first day
          thinned.push(sortedDays[firstIndex]);

          // Include days with data (non-zero values)
          const daysWithData = sortedDays.filter(
            (day, index) =>
              day.value > 0 && index !== firstIndex && index !== lastIndex
          );

          // Add days with data first
          daysWithData.forEach((day) => {
            if (!thinned.find((d) => d.dayKey === day.dayKey)) {
              thinned.push(day);
            }
          });

          // Calculate how many more days we need (target ~12 days total including first and last)
          const targetDays = 12;
          const currentCount = thinned.length + 1; // +1 for last day we'll add
          const daysNeeded = Math.max(0, targetDays - currentCount);

          // Add evenly spaced days to fill remaining slots
          if (daysNeeded > 0 && sortedDays.length > 2) {
            const spacing = Math.max(
              1,
              Math.floor((sortedDays.length - 2) / daysNeeded)
            );
            for (
              let i = 1;
              i < sortedDays.length - 1 && thinned.length < targetDays - 1;
              i += spacing
            ) {
              const day = sortedDays[i];
              // Don't add if already included
              if (!thinned.find((d) => d.dayKey === day.dayKey)) {
                thinned.push(day);
              }
            }
          }

          // Always include last day
          if (!thinned.find((d) => d.dayKey === sortedDays[lastIndex].dayKey)) {
            thinned.push(sortedDays[lastIndex]);
          }

          // Ensure we have at least a minimum number of days (fallback)
          if (thinned.length < 5) {
            // If thinning resulted in too few days, show every Nth day
            const fallbackSpacing = Math.max(
              1,
              Math.floor(sortedDays.length / 10)
            );
            const fallback = [sortedDays[firstIndex]];
            for (
              let i = fallbackSpacing;
              i < sortedDays.length - 1;
              i += fallbackSpacing
            ) {
              fallback.push(sortedDays[i]);
            }
            fallback.push(sortedDays[lastIndex]);
            return fallback
              .sort((a, b) => a.timestamp - b.timestamp)
              .map((entry) => ({ name: entry.name, value: entry.value }));
          }

          // Sort by timestamp again after thinning
          return thinned
            .sort((a, b) => a.timestamp - b.timestamp)
            .map((entry) => ({ name: entry.name, value: entry.value }));
        }

        return sortedDays.map((entry) => ({
          name: entry.name,
          value: entry.value,
        }));
        */

      case "year": {
        // Last 12 months by month
        const monthsAgo12 = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        filteredData = filteredData.filter((item) => {
          const itemDate = new Date(item.createdAt);
          return itemDate >= monthsAgo12 && itemDate <= now;
        });

        // Initialize 12 months chronologically
        const monthData = [];
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = `${date.getFullYear()}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}`;
          const monthName = monthNames[date.getMonth()];
          monthData.push({
            monthKey,
            timestamp: date.getTime(),
            name: monthName,
            value: 0,
          });
        }

        // Group by month
        filteredData.forEach((item) => {
          const itemDate = new Date(item.createdAt);
          const monthKey = `${itemDate.getFullYear()}-${String(
            itemDate.getMonth() + 1
          ).padStart(2, "0")}`;
          const monthEntry = monthData.find((m) => m.monthKey === monthKey);
          if (monthEntry) {
            monthEntry.value += 1;
          }
        });

        // Sort chronologically and intelligently select months to display
        const sortedMonths = monthData.sort(
          (a, b) => a.timestamp - b.timestamp
        );

        // If we have many months, show every 3rd month (Jan, Apr, Jul, Oct) plus current month
        // Otherwise show all months
        const shouldThin = sortedMonths.length > 8;
        if (shouldThin) {
          const thinned = [];
          const currentMonthIndex = sortedMonths.length - 1;
          for (let i = 0; i < sortedMonths.length; i++) {
            // Always include first, last, and every 3rd month
            if (i === 0 || i === currentMonthIndex || i % 3 === 0) {
              thinned.push(sortedMonths[i]);
            }
          }
          return thinned.map((entry) => ({
            name: entry.name,
            value: entry.value,
          }));
        }

        return sortedMonths.map((entry) => ({
          name: entry.name,
          value: entry.value,
        }));
      }

      default:
        return null;
    }
  };

  const getDefaultData = (type) => {
    switch (type) {
      case "year":
        return [
          { name: "Jan", value: 0 },
          { name: "Feb", value: 0 },
          { name: "Mar", value: 0 },
          { name: "Apr", value: 0 },
          { name: "May", value: 0 },
          { name: "Jun", value: 0 },
          { name: "Jul", value: 0 },
          { name: "Aug", value: 0 },
          { name: "Sep", value: 0 },
          { name: "Oct", value: 0 },
          { name: "Nov", value: 0 },
          { name: "Dec", value: 0 },
        ];
      case "month":
        // 30 days default - create chronologically with proper day labels
        const monthDefault = [];
        for (let i = 29; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          monthDefault.push({
            name: String(date.getDate()),
            value: 0,
            timestamp: date.getTime(),
          });
        }
        // Sort by timestamp to ensure chronological order
        return monthDefault
          .sort((a, b) => a.timestamp - b.timestamp)
          .map(({ name, value }) => ({ name, value }));
      case "week":
        return [
          { name: "Sun", value: 0 },
          { name: "Mon", value: 0 },
          { name: "Tue", value: 0 },
          { name: "Wed", value: 0 },
          { name: "Thu", value: 0 },
          { name: "Fri", value: 0 },
          { name: "Sat", value: 0 },
        ];
      case "day":
        return Array.from({ length: 24 }, (_, i) => ({
          name: `${String(i).padStart(2, "0")}:00`,
          value: 0,
        }));
      default:
        return [
          { name: "Sun", value: 0 },
          { name: "Mon", value: 0 },
          { name: "Tue", value: 0 },
          { name: "Wed", value: 0 },
          { name: "Thu", value: 0 },
          { name: "Fri", value: 0 },
          { name: "Sat", value: 0 },
        ];
    }
  };

  // Helper function to sort fallback data chronologically
  const sortDataChronologically = (data, period) => {
    if (!data || !Array.isArray(data) || data.length === 0) return data;

    const sorted = [...data];
    switch (period) {
      case "day":
        // Sort by hour (00:00, 01:00, ..., 23:00)
        return sorted.sort((a, b) => {
          const hourA = parseInt(a.name?.split(":")[0] || 0);
          const hourB = parseInt(b.name?.split(":")[0] || 0);
          return hourA - hourB;
        });
      case "week":
        // Sort by day name (Sun=0, Mon=1, ..., Sat=6)
        const dayOrder = {
          Sun: 0,
          Mon: 1,
          Tue: 2,
          Wed: 3,
          Thu: 4,
          Fri: 5,
          Sat: 6,
        };
        return sorted.sort((a, b) => {
          return (dayOrder[a.name] ?? 99) - (dayOrder[b.name] ?? 99);
        });
      case "month":
        // Check if data contains week numbers instead of day numbers
        const hasWeekNumbers = sorted.some(
          (item) =>
            item.name &&
            (item.name.toLowerCase().includes("week") ||
              item.name.match(/week\s*\d+/i))
        );

        if (hasWeekNumbers) {
          // If API returned week data for month view, map it to days
          // Helper function to get week number from date
          const getWeekNumber = (date) => {
            const d = new Date(
              Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
            );
            const dayNum = d.getUTCDay() || 7;
            d.setUTCDate(d.getUTCDate() + 4 - dayNum);
            const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
            return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
          };

          const now = new Date();
          const dayData = [];

          // Extract week data with values
          const weekDataMap = new Map();
          sorted.forEach((item) => {
            const weekMatch = item.name.match(/week\s*(\d+)/i);
            if (weekMatch) {
              const weekNum = parseInt(weekMatch[1]);
              weekDataMap.set(weekNum, item.value || 0);
            }
          });

          // Create 30 days structure and map to weeks
          for (let i = 29; i >= 0; i--) {
            const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
            const dayLabel = date.getDate();
            const weekNum = getWeekNumber(date);

            // Get week value and distribute evenly across 7 days
            const weekValue = weekDataMap.get(weekNum) || 0;
            const dayValue = Math.round(weekValue / 7);

            dayData.push({
              name: String(dayLabel),
              value: dayValue,
              timestamp: date.getTime(),
            });
          }

          // Sort chronologically
          const sortedDays = dayData
            .sort((a, b) => a.timestamp - b.timestamp)
            .map(({ name, value }) => ({ name, value }));

          // Apply smart thinning: show important days only
          const thinned = [];
          const maxPoints = 15; // Maximum number of points to show

          // Always include first and last day
          if (sortedDays.length > 0) {
            thinned.push(sortedDays[0]);
          }

          // Find days with data (non-zero) and days where value changes
          const importantDays = new Set([0]); // First day index

          for (let i = 1; i < sortedDays.length - 1; i++) {
            const prev = sortedDays[i - 1];
            const curr = sortedDays[i];
            const next = sortedDays[i + 1];

            // Include if:
            // 1. Has clicks (value > 0)
            // 2. Value changes from previous or to next (peak/valley)
            if (
              curr.value > 0 ||
              curr.value !== prev.value ||
              curr.value !== next.value
            ) {
              importantDays.add(i);
            }
          }

          // Always include last day
          if (sortedDays.length > 1) {
            importantDays.add(sortedDays.length - 1);
          }

          // If we have too many important days, thin further
          if (importantDays.size > maxPoints) {
            // Keep first, last, and evenly spaced important days
            const importantArray = Array.from(importantDays).sort(
              (a, b) => a - b
            );
            const step = Math.ceil(importantArray.length / maxPoints);
            const thinnedIndices = new Set();

            // Always include first and last
            thinnedIndices.add(importantArray[0]);
            thinnedIndices.add(importantArray[importantArray.length - 1]);

            // Include evenly spaced important days
            for (let i = step; i < importantArray.length - 1; i += step) {
              thinnedIndices.add(importantArray[i]);
            }

            // Fill gaps: ensure we don't skip too many consecutive days
            for (let i = 1; i < importantArray.length - 1; i++) {
              const curr = importantArray[i];
              const prev = importantArray[i - 1];
              const next = importantArray[i + 1];

              // If gap is too large, include middle point
              if (curr - prev > 5 && !thinnedIndices.has(curr)) {
                thinnedIndices.add(Math.floor((curr + prev) / 2));
              }
              if (next - curr > 5 && !thinnedIndices.has(curr)) {
                thinnedIndices.add(Math.floor((curr + next) / 2));
              }
            }

            return sortedDays
              .filter((_, index) => thinnedIndices.has(index))
              .map(({ name, value }) => ({ name, value }));
          }

          // Return important days
          return sortedDays
            .filter((_, index) => importantDays.has(index))
            .map(({ name, value }) => ({ name, value }));
        }

        // Sort by day number (1, 2, 3, ..., 30)
        return sorted.sort((a, b) => {
          const dayA = parseInt(a.name) || 0;
          const dayB = parseInt(b.name) || 0;
          return dayA - dayB;
        });
      case "year":
        // Sort by month name (Jan, Feb, Mar, ..., Dec)
        const monthOrder = {
          Jan: 0,
          Feb: 1,
          Mar: 2,
          Apr: 3,
          May: 4,
          Jun: 5,
          Jul: 6,
          Aug: 7,
          Sep: 8,
          Oct: 9,
          Nov: 10,
          Dec: 11,
        };
        return sorted.sort((a, b) => {
          return (monthOrder[a.name] ?? 99) - (monthOrder[b.name] ?? 99);
        });
      default:
        return sorted;
    }
  };

  const getData = () => {
    // Try to process raw data first
    const processedData = processDataByPeriod(view);
    if (processedData && processedData.length > 0) {
      return processedData;
    }

    // Fall back to API-provided aggregated data
    let data;
    switch (view) {
      case "year":
        data = dataYearly;
        break;
      case "month":
        data = dataMonthly;
        break;
      case "week":
        data = dataWeekly;
        break;
      case "day":
        data = dataDaily;
        break;
      default:
        data = dataWeekly;
    }

    // Sort fallback data chronologically before returning
    if (data && Array.isArray(data) && data.length > 0) {
      const sortedData = sortDataChronologically(data, view);
      // If sorting returned null (e.g., month view has week data), use default
      if (sortedData === null) {
        return getDefaultData(view);
      }

      return sortedData;
    }

    const defaultData = getDefaultData(view);

    return defaultData;
  };

  // Calculate dynamic Y-axis domain
  const chartData = useMemo(() => {
    const data = getData();

    return data;
  }, [view, rawData, dataYearly, dataMonthly, dataWeekly, dataDaily]);
  const maxValue = useMemo(() => {
    if (!chartData || chartData.length === 0) return 10;
    const max = Math.max(...chartData.map((item) => item.value || 0));
    // If max is 0, still show a reasonable scale so the chart is visible
    return max === 0 ? 10 : Math.max(max * 1.1, 10);
  }, [chartData]);

  // Simple tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "4px",
            padding: "8px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            fontSize: "14px",
          }}
        >
          <div style={{ fontWeight: "bold", color: "#333" }}>
            {label}: {value} {value === 1 ? "click" : "clicks"}
          </div>
        </div>
      );
    }
    return null;
  };

  // Table columns configuration
  const getTableColumns = () => [
    {
      title: "Period",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Clicks",
      dataIndex: "value",
      key: "value",
      sorter: (a, b) => a.value - b.value,
    },
  ];

  // Table data preparation
  const getTableData = () => {
    return chartData.map((item, index) => ({
      ...item,
      key: index,
    }));
  };

  return (
    <Card
      title={title}
      bordered={false}
      // extra={
      // <DatePicker
      //   onChange={handleDateChange}
      //   defaultValue={date ? moment(date) : moment()} // Ensure date is in the correct format
      //   picker="date"  // Make sure it's always the date picker, not year or month picker
      // />
      //   <input
      //     className="calender"
      //     onChange={handleDateChange}
      //     value={date}
      //     type="date"
      //   ></input>
      // }
      // extra={<DatePicker picker={view === 'yearly' ? 'year' : view === 'monthly' ? 'month' : 'date'} onChange={handleDateChange} defaultValue={date} />}
      // extra={<RangePicker
      //     showTime

      //     format="YYYY-MM-DD"

      //     // style={{ width: '100%' }}
      //     // className="inputbox custom-range-picker"
      //     onChange={handleDateChange} defaultValue={date}
      // />}

      className={`chart-card ${selected ? "selected" : ""}`}
      onClick={onClick}
    >
      <div
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        <Radio.Group value={view} onChange={handleViewChange}>
          <Radio.Button value="year">Year</Radio.Button>
          <Radio.Button value="month">Month</Radio.Button>
          <Radio.Button value="week">Week</Radio.Button>
          <Radio.Button value="day">Day</Radio.Button>
        </Radio.Group>

        <Radio.Group
          value={displayType}
          onChange={(e) => setDisplayType(e.target.value)}
          size="small"
        >
          <Radio.Button value="chart">📈 Chart</Radio.Button>
          <Radio.Button value="table">📋 Table</Radio.Button>
        </Radio.Group>
      </div>
      {displayType === "table" ? (
        <Table
          columns={getTableColumns()}
          dataSource={getTableData()}
          pagination={false}
          size="small"
        />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            width={500}
            height={300}
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, maxValue]} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color || "#15803D"}
              strokeDasharray="3 4 5 2"
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              name={dataKey || "Clicks"}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      {/* <div className="d-flex">
        <p
          className={`chart-card ${
            colornumber === 0
              ? "redbox"
              : colornumber === 1
              ? "greenbox"
              : "nobox"
          }`}
        ></p>

        <p className="boxpara">User Who Clicked</p>
      </div> */}
    </Card>
  );
};

export default ChartCard;
